/**
 * Movement Module - Abstract movement functions
 * 
 * **INTERNAL CLASSES - DO NOT USE DIRECTLY**
 * Use TransformMovementManager or PhysicsMovementManager instead.
 * 
 * This file contains the internal implementation classes:
 * - Movement: Base class with core movement logic
 * - TransformMovement: Direct transform manipulation (kinematic)
 * - PhysicsMovement: Physics-based movement (forces/velocities)
 * 
 * Uses Three.js Vector2 for all vector operations.
 */

import { Vector2, Vector3, Object3D } from 'three';
import { Body, Vec3 } from 'cannon-es';


/**
 * Movement result containing all movement data
 */
export interface MovementResult {
    /** Normalized direction vector */
    direction: Vector2;
    /** Velocity vector (direction * speed * deltaTime) */
    velocity: Vector2;
    /** Current speed being applied */
    speed: number;
    /** Whether movement is active */
    isMoving: boolean;
}

/**
 * Movement configuration
 */
export interface MovementConfig {
    /** Base movement speed (units per second) */
    speed?: number;
    /** Sprint speed multiplier */
    sprintMultiplier?: number;
    /** Normalize diagonal movement to prevent faster diagonal speed */
    normalizeDiagonal?: boolean;
    /** Jump force/velocity (default: 5.0) */
    jumpForce?: number;
    /** Gravity for transform-based movement (default: -9.81) */
    gravity?: number;
}

/**
 * Base Movement Class
 * 
 * **INTERNAL CLASS - DO NOT USE DIRECTLY**
 * Use TransformMovementManager or PhysicsMovementManager instead.
 * 
 * Provides core movement calculation functionality.
 */
export class Movement {
    protected config: Required<MovementConfig>;

    constructor(config: MovementConfig = {}) {
        this.config = {
            speed: config.speed ?? 5.0,
            sprintMultiplier: config.sprintMultiplier ?? 2.0,
            normalizeDiagonal: config.normalizeDiagonal ?? true,
            jumpForce: config.jumpForce ?? 5.0,
            gravity: config.gravity ?? -9.81,
        };
    }

    /**
     * Calculate movement from input values
     * 
     * @param forwardInput - Forward/backward input (-1 to 1, where 1 = forward, -1 = backward)
     * @param strafeInput - Left/right input (-1 to 1, where 1 = right, -1 = left)
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame (for frame-independent movement)
     * @returns Movement result with velocity and direction
     */
    calculateMovement(
        forwardInput: number,
        strafeInput: number,
        isSprinting: boolean = false,
        deltaTime: number = 1
    ): MovementResult {
        // Create direction vector from inputs
        const direction = new Vector2(strafeInput, forwardInput);

        // Check if there's any movement input
        const isMoving = direction.x !== 0 || direction.y !== 0;

        // Normalize diagonal movement to prevent faster diagonal speed
        if (isMoving && this.config.normalizeDiagonal) {
            direction.normalize();
        }

        // Calculate current speed (apply sprint multiplier if active)
        const currentSpeed = isSprinting
            ? this.config.speed * this.config.sprintMultiplier
            : this.config.speed;

        // Calculate velocity (direction * speed * deltaTime)
        const velocity = direction.clone().multiplyScalar(currentSpeed * deltaTime);

        return {
            direction: direction.clone(),
            velocity,
            speed: currentSpeed,
            isMoving,
        };
    }

    /**
     * Calculate movement from a vector input
     * Useful when you already have a combined input vector
     * 
     * @param inputVector - Input vector { x: strafe, y: forward } (can be Vector2 or Vector2-like)
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @returns Movement result
     */
    calculateMovementFromVector(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1
    ): MovementResult {
        return this.calculateMovement(
            inputVector.y,
            inputVector.x,
            isSprinting,
            deltaTime
        );
    }

    /**
     * Update movement speed
     */
    setSpeed(speed: number): void {
        this.config.speed = speed;
    }

    /**
     * Get current base speed
     */
    getSpeed(): number {
        return this.config.speed;
    }

    /**
     * Update sprint multiplier
     */
    setSprintMultiplier(multiplier: number): void {
        this.config.sprintMultiplier = multiplier;
    }

    /**
     * Get current sprint multiplier
     */
    getSprintMultiplier(): number {
        return this.config.sprintMultiplier;
    }

    /**
     * Enable/disable diagonal normalization
     */
    setNormalizeDiagonal(normalize: boolean): void {
        this.config.normalizeDiagonal = normalize;
    }

    /**
     * Get current configuration
     */
    getConfig(): Readonly<Required<MovementConfig>> {
        return { ...this.config };
    }

    /**
     * Apply external rotation (e.g., camera yaw) to velocity vector
     * 
     * Low-level utility that rotates a 2D velocity vector by an external angle.
     * Used internally by higher-level convenience methods.
     * 
     * **Most users should use instead:**
     * - `TransformMovement.applyCameraRelativeMovement()` for kinematic movement
     * - `PhysicsMovement.getPhysicsVelocityWithRotation()` for physics movement
     * 
     * @param velocity - 2D velocity vector (x: strafe, y: forward)
     * @param yawAngle - External yaw angle in radians (e.g., camera yaw)
     * @returns Rotated velocity in 3D (x, z) coordinates
     * 
     * @internal This is a low-level utility. Use high-level methods when possible.
     */
    protected applyExternalRotationToVelocity(velocity: Vector2, yawAngle: number): { x: number; z: number } {
        // Calculate input angle from the velocity
        const inputAngle = Math.atan2(-velocity.x, velocity.y);

        // Combine input angle with external yaw
        const targetAngle = inputAngle + yawAngle;

        // Get the magnitude (speed) of the velocity
        const speed = velocity.length();

        // Calculate movement direction from combined angle
        return {
            x: Math.sin(targetAngle) * speed,
            z: Math.cos(targetAngle) * speed,
        };
    }

    /**
  * Apply rotation to velocity vector
  * Transforms velocity from local space to world space based on object's Y rotation
  * 
  * @param velocity - 2D velocity vector (x: strafe, y: forward)
  * @returns Rotated velocity in 3D (x, z)
  */
    protected applyRotationToVelocity(target: Object3D, velocity: Vector2): { x: number; z: number } {
        // Get forward direction from the player's world direction
        const forward = new Vector3();
        target.getWorldDirection(forward);
        forward.y = 0; // Keep movement on horizontal plane
        forward.normalize();

        // Get right direction (perpendicular to forward)
        const right = new Vector3();
        right.crossVectors(forward, new Vector3(0, 1, 0));
        right.normalize();

        // Combine forward and right movements based on input
        // velocity.y = forward/backward input
        // velocity.x = strafe left/right input
        const moveDirection = new Vector3();
        moveDirection.addScaledVector(forward, velocity.y);
        moveDirection.addScaledVector(right, velocity.x);

        return {
            x: moveDirection.x,
            z: moveDirection.z,
        };
    }

    /**
     * Smoothly rotate an object towards a target angle
     * 
     * General-purpose rotation helper that works with any Three.js Object3D.
     * Calculates the shortest rotation path and applies smooth interpolation.
     * 
     * @param object - Three.js Object3D to rotate (mesh, group, etc.)
     * @param targetAngle - Target angle in radians (Y rotation)
     * @param smoothFactor - Smoothing factor (0 = instant, 1 = no rotation, 0.1-0.2 recommended)
     * @returns The angle difference that was applied
     * 
     * @example
     * ```typescript
     * // Rotate player to face movement direction
     * const targetAngle = Math.atan2(velocityX, velocityZ);
     * movement.smoothRotateTowards(playerMesh, targetAngle, 0.15);
     * ```
     */
    public smoothRotateTowards(object: Object3D, targetAngle: number, smoothFactor: number = 0.15): number {
        // Calculate shortest rotation difference
        const rotationDiff = targetAngle - object.rotation.y;
        const normalizedDiff = Math.atan2(Math.sin(rotationDiff), Math.cos(rotationDiff));

        // Apply smooth rotation
        const appliedRotation = normalizedDiff * smoothFactor;
        object.rotation.y += appliedRotation;

        return appliedRotation;
    }

    update(deltaTime: number) {
        console.log('Movement updated', deltaTime);
    }
}

/**
 * Transform-Based Movement
 * 
 * **INTERNAL CLASS - DO NOT USE DIRECTLY**
 * Use TransformMovementManager instead, which provides a clean facade.
 * 
 * Directly manipulates Three.js Object3D transforms.
 * For kinematic movement, non-physics games, camera controllers.
 * 
 * Provides both instant and lerped (smooth) movement options.
 * Includes gravity and jumping for transform-based physics simulation.
 */
export class TransformMovement extends Movement {
    // Target object to apply movement to
    private target: Object3D;

    // Store current velocity for lerping
    private currentVelocity: Vector2 = new Vector2(0, 0);

    // Vertical velocity for jumping/gravity
    private verticalVelocity: number = 0;

    // Ground detection
    private grounded: boolean = true;

    constructor(
        target: Object3D,
        config: MovementConfig = {}
    ) {
        super(config);
        this.target = target;
    }
    /**
     * Apply movement directly to the target object
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param mapYToZ - Map Y input to Z position (default: true for 3D games)
     * @param useRotation - Apply movement relative to object's rotation (default: false = world-space)
     * @param externalYaw - Optional external yaw angle (in radians) to use instead of object's rotation (e.g., camera yaw for third-person)
     * @returns Movement result
     */
    applyMovement(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        mapYToZ: boolean = true,
        useRotation: boolean = false,
        externalYaw?: number
    ): MovementResult {
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            deltaTime
        );

        if (result.isMoving) {
            if (externalYaw !== undefined && mapYToZ) {
                // External rotation-relative movement (e.g., camera-relative for third-person)
                const velocity = this.applyExternalRotationToVelocity(result.velocity, externalYaw);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else if (useRotation && mapYToZ) {
                // Object rotation-relative movement (relative to object's Y rotation)
                const velocity = this.applyRotationToVelocity(this.target, result.velocity);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else {
                // World-space movement (fixed directions)
                this.target.position.x += result.velocity.x;

                if (mapYToZ) {
                    // For 3D games: Y input maps to Z position
                    this.target.position.z -= result.velocity.y;
                } else {
                    // For 2D games: Y input maps to Y position
                    this.target.position.y += result.velocity.y;
                }
            }
        }


        return result;
    }

    /**
     * Apply movement with manual input values
     * 
     * @param forwardInput - Forward/backward input (-1 to 1)
     * @param strafeInput - Left/right input (-1 to 1)
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param mapYToZ - Map Y input to Z position (default: true)
     * @param useRotation - Apply movement relative to object's rotation (default: false = world-space)
     * @param externalYaw - Optional external yaw angle (in radians) to use instead of object's rotation
     * @returns Movement result
     */
    applyMovementRaw(
        forwardInput: number,
        strafeInput: number,
        isSprinting: boolean = false,
        deltaTime: number = 1,
        mapYToZ: boolean = true,
        useRotation: boolean = false,
        externalYaw?: number
    ): MovementResult {
        const result = this.calculateMovement(
            forwardInput,
            strafeInput,
            isSprinting,
            deltaTime
        );

        if (result.isMoving) {
            if (externalYaw !== undefined && mapYToZ) {
                // External rotation-relative movement
                const velocity = this.applyExternalRotationToVelocity(result.velocity, externalYaw);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else if (useRotation && mapYToZ) {
                // Object rotation-relative movement
                const velocity = this.applyRotationToVelocity(this.target, result.velocity);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else {
                // World-space movement
                this.target.position.x += result.velocity.x;

                if (mapYToZ) {
                    this.target.position.z -= result.velocity.y;
                } else {
                    this.target.position.y += result.velocity.y;
                }
            }
        }


        return result;
    }

    /**
     * Apply movement with lerp (smooth interpolation)
     * 
     * PRIVATE: Use applyWorldMovement, applyTransformRelativeMovement, or applyCameraRelativeMovement instead.
     * 
     * Provides smooth acceleration and deceleration.
     * Better for polished games - prevents instant start/stop.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param lerpFactor - Smoothing factor (0 = instant, 1 = no movement, 0.1-0.2 recommended)
     * @param mapYToZ - Map Y input to Z position (default: true)
     * @param useRotation - Apply movement relative to object's rotation (default: false = world-space)
     * @param externalYaw - Optional external yaw angle (in radians) to use instead of object's rotation
     * @returns Movement result (with lerped velocity)
     */
    private applyMovementLerp(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        lerpFactor: number = 0.15,
        mapYToZ: boolean = true,
        useRotation: boolean = false,
        externalYaw?: number
    ): MovementResult {
        // Calculate target velocity
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            deltaTime
        );

        // Lerp current velocity towards target velocity
        this.currentVelocity.lerp(result.velocity, 1 - lerpFactor);

        // Apply lerped velocity
        if (this.currentVelocity.length() > 0.001) { // Threshold to stop small movements
            if (externalYaw !== undefined && mapYToZ) {
                // External rotation-relative movement
                const velocity = this.applyExternalRotationToVelocity(this.currentVelocity, externalYaw);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else if (useRotation && mapYToZ) {
                // Object rotation-relative movement
                const velocity = this.applyRotationToVelocity(this.target, this.currentVelocity);
                this.target.position.x += velocity.x;
                this.target.position.z += velocity.z;
            } else {
                // World-space movement
                this.target.position.x += this.currentVelocity.x;

                if (mapYToZ) {
                    this.target.position.z -= this.currentVelocity.y;
                } else {
                    this.target.position.y += this.currentVelocity.y;
                }
            }
        }


        // Return result with lerped velocity
        return {
            direction: result.direction,
            velocity: this.currentVelocity.clone(),
            speed: this.currentVelocity.length(),
            isMoving: result.isMoving,
        };
    }

    /**
     * Reset lerp velocity (useful when teleporting or changing state)
     */
    resetVelocity(): void {
        this.currentVelocity.set(0, 0);
    }

    /**
     * Get current lerped velocity
     */
    getCurrentVelocity(): Vector2 {
        return this.currentVelocity.clone();
    }


    /**
     * Apply camera-relative movement with automatic character rotation
     * Convenience method that combines external yaw movement + character rotation
     * Perfect for Unity-style third-person controls
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param cameraYaw - Camera yaw angle in radians
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param movementLerpFactor - Movement smoothing (0.15 recommended)
     * @param rotationSmoothFactor - Rotation smoothing (0.15 recommended)
     * @returns Movement result
     * 
     * @example
     * ```typescript
     * // Third-person Unity-style movement (one-liner!)
     * const cameraYaw = cameraManager.getYaw();
     * movement.applyCameraRelativeMovement(inputVector, cameraYaw, isSprinting, deltaTime);
     * ```
     */
    applyCameraRelativeMovement(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw: number,
        isSprinting: boolean = false,
        deltaTime: number = 1,
        movementLerpFactor: number = 0.15,
        rotationSmoothFactor: number = 0.15
    ): MovementResult {
        const isMoving = inputVector.x !== 0 || inputVector.y !== 0;

        // Apply camera-relative movement
        const result = this.applyMovementLerp(
            inputVector,
            isSprinting,
            deltaTime,
            movementLerpFactor,
            true,  // mapYToZ
            false, // useRotation (we use externalYaw instead)
            cameraYaw
        );

        // Rotate character towards movement direction (only when moving)
        if (isMoving) {
            const inputAngle = Math.atan2(-inputVector.x, inputVector.y);
            const targetAngle = inputAngle + cameraYaw;
            this.smoothRotateTowards(this.target, targetAngle, rotationSmoothFactor);
        }

        return result;
    }

    /**
     * Apply transform-relative movement with smooth interpolation
     * 
     * Movement is relative to the object's current rotation.
     * "Forward" always moves in the direction the object is facing.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param lerpFactor - Movement smoothing factor (0.15 recommended)
     * @returns Movement result
     * 
     * @example
     * ```typescript
     * // Object moves in its own facing direction
     * movement.applyTransformRelativeMovement(inputVector, isSprinting, deltaTime);
     * ```
     */
    applyTransformRelativeMovement(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        lerpFactor: number = 0.15
    ): MovementResult {
        // Use applyMovementLerp with useRotation=true
        return this.applyMovementLerp(
            inputVector,
            isSprinting,
            deltaTime,
            lerpFactor,
            true,  // mapYToZ
            true   // useRotation (object-relative)
        );
    }

    /**
     * Apply world-space movement with smooth interpolation
     * 
     * Movement is in fixed world-space directions (north/south/east/west).
     * Object rotation does not affect movement direction.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param lerpFactor - Movement smoothing factor (0.15 recommended)
     * @returns Movement result
     * 
     * @example
     * ```typescript
     * // Object moves in world-space directions (useful for top-down games)
     * movement.applyWorldMovement(inputVector, isSprinting, deltaTime);
     * ```
     */
    applyWorldMovement(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        lerpFactor: number = 0.15
    ): MovementResult {
        // Use applyMovementLerp with useRotation=false
        return this.applyMovementLerp(
            inputVector,
            isSprinting,
            deltaTime,
            lerpFactor,
            true,   // mapYToZ
            false   // useRotation (world-space)
        );
    }

    /**
     * Apply gravity and vertical movement to the target object
     * Call this every frame to simulate gravity and jumping.
     * 
     * Note: Grounded state should be set by external collision detection using setGrounded()
     * Only applies gravity if NOT grounded.
     * 
     * @param deltaTime - Time since last frame
     * @returns Current vertical velocity
     */
    applyGravity(deltaTime: number): number {
        // Only apply gravity if not grounded
        if (!this.grounded) {
            // Apply gravity
            this.verticalVelocity += this.config.gravity * deltaTime;

            // Apply vertical velocity
            this.target.position.y += this.verticalVelocity * deltaTime;
        } else {
            // Grounded - zero out vertical velocity to prevent going through ground
            this.verticalVelocity = 0;
        }

        return this.verticalVelocity;
    }

    /**
     * Jump with a specific power/force
     * Height is determined by the power applied.
     * 
     * @param jumpPower - Jump power/velocity (default: uses config.jumpForce)
     * @returns True if jump was executed
     */
    jumpWithPower(jumpPower?: number): boolean {
        if (!this.grounded) {
            return false;
        }

        // Apply upward velocity
        this.verticalVelocity = jumpPower ?? this.config.jumpForce;

        // Set grounded to false immediately so gravity can take effect
        this.grounded = false;

        return true;
    }

    /**
     * Jump to a specific height
     * Calculates required velocity to reach the target height.
     * Uses physics formula: v = sqrt(2 * g * h)
     * 
     * @param targetHeight - Desired jump height in units
     * @returns True if jump was executed
     */
    jumpToHeight(targetHeight: number): boolean {
        if (!this.grounded) {
            return false;
        }

        // Calculate required initial velocity to reach target height
        // Using: v = sqrt(2 * |gravity| * height)
        const gravity = Math.abs(this.config.gravity);
        const requiredVelocity = Math.sqrt(2 * gravity * targetHeight);

        // Apply upward velocity
        this.verticalVelocity = requiredVelocity;

        // Set grounded to false immediately so gravity can take effect
        this.grounded = false;

        return true;
    }

    /**
     * Default jump function (uses power from config)
     * Alias for jumpWithPower() with default config value
     * 
     * @returns True if jump was executed
     */
    jump(): boolean {
        return this.jumpWithPower();
    }

    /**
     * Check if object is on the ground
     */
    isGrounded(): boolean {
        return this.grounded;
    }

    /**
     * Get current vertical velocity
     */
    getVerticalVelocity(): number {
        return this.verticalVelocity;
    }

    /**
     * Set grounded state (called by collision detection module)
     * 
     * @param grounded - Whether object is on the ground
     * @param resetVelocity - Whether to reset vertical velocity when grounded (default: true)
     */
    setGrounded(grounded: boolean, resetVelocity: boolean = true): void {
        this.grounded = grounded;
        if (grounded && resetVelocity) {
            this.verticalVelocity = 0;
        }
    }

    /**
     * Reset all velocities (horizontal and vertical)
     */
    resetAllVelocity(): void {
        this.currentVelocity.set(0, 0);
        this.verticalVelocity = 0;
    }

    /**
     * Get the target object
     */
    getTarget(): Object3D {
        return this.target;
    }

    /**
     * Set a new target object
     */
    setTarget(target: Object3D): void {
        this.target = target;
    }



}

/**
 * Physics velocity data for 3D physics engines
 */
export interface PhysicsVelocityData {
    /** Velocity for X axis (strafe) */
    x: number;
    /** Velocity for Z axis (forward/backward in 3D) */
    z: number;
    /** Original 2D velocity vector */
    velocity2D: Vector2;
    /** Direction vector */
    direction: Vector2;
    /** Current speed */
    speed: number;
    /** Whether moving */
    isMoving: boolean;
}

/**
 * Physics-Based Movement
 * 
 * **INTERNAL CLASS - DO NOT USE DIRECTLY**
 * Use PhysicsMovementManager instead, which provides a clean facade.
 * 
 * Provides movement calculations and helpers for physics engines.
 * Returns velocity/force data to apply to physics bodies.
 * Includes jumping functionality for Cannon.js bodies.
 * 
 * Can optionally take a physics body reference in constructor for convenience methods.
 */
export class PhysicsMovement extends Movement {
    private lastJumpTime: number = 0;
    private jumpCooldown: number = 0.2; // 200ms cooldown between jumps
    private body: Body | null = null; // Optional physics body reference
    private mesh: Object3D | null = null; // Optional mesh reference for rotation

    constructor(config: MovementConfig = {}, body?: Body, mesh?: Object3D) {
        super(config);
        if (body) {
            this.body = body;
        }
        if (mesh) {
            this.mesh = mesh;
        }
    }

    /**
     * Set the physics body reference
     * Allows using convenience methods without passing body parameter
     */
    setBody(body: Body): void {
        this.body = body;
    }

    /**
     * Get the current physics body reference
     */
    getBody(): Body | null {
        return this.body;
    }

    /**
     * Set the mesh reference
     * Allows using rotation methods without passing mesh parameter
     */
    setMesh(mesh: Object3D): void {
        this.mesh = mesh;
    }

    /**
     * Get the current mesh reference
     */
    getMesh(): Object3D | null {
        return this.mesh;
    }

    /**
     * Apply movement to the stored physics body (convenience method)
     * 
     * Requires body to be set via constructor or setBody().
     * Automatically applies velocity with camera rotation.
     * Does NOT rotate the mesh - for physics-based rotation or manual control.
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param cameraYaw - Camera yaw angle in radians
     * @param isSprinting - Whether sprint is active
     * @returns Physics velocity data
     * @throws Error if body is not set
     * 
     * @example
     * ```typescript
     * const movement = new PhysicsMovement({ speed: 5.0 }, cannonBody);
     * movement.applyMovementWithRotation(inputVector, cameraYaw, isSprinting);
     * ```
     */
    applyMovementWithRotation(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw: number,
        isSprinting: boolean = false
    ): PhysicsVelocityData {
        if (!this.body) {
            throw new Error('PhysicsMovement: No body set. Use setBody() or pass body in constructor.');
        }

        const velocityData = this.getPhysicsVelocityWithRotation(inputVector, cameraYaw, isSprinting);

        // Apply velocity to body (preserve Y)
        this.body.velocity.x = velocityData.x;
        this.body.velocity.z = velocityData.z;

        return velocityData;
    }

    /**
     * Apply movement with automatic mesh rotation (full convenience method)
     * 
     * Like applyCameraRelativeMovement() but for physics bodies.
     * Applies velocity AND rotates the visual mesh to face movement direction.
     * 
     * Supports three movement modes:
     * 1. External camera rotation (cameraYaw provided) - movement relative to camera
     * 2. Object rotation (useRotation=true) - movement relative to mesh's own rotation
     * 3. World space (no cameraYaw, useRotation=false) - movement in world coordinates
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param cameraYaw - Optional camera yaw angle in radians (for camera-relative movement)
     * @param isSprinting - Whether sprint is active
     * @param rotationSmooth - Rotation smoothing factor (0.15 recommended)
     * @param useRotation - Use mesh's own rotation for movement direction (when cameraYaw not provided)
     * @param mesh - Optional Three.js mesh/group to rotate (uses stored mesh if not provided)
     * @returns Physics velocity data
     * @throws Error if body is not set
     * @throws Error if mesh is not set and not provided
     * 
     * @example
     * ```typescript
     * // Camera-relative movement (mesh auto-rotates to face direction)
     * const movement = new PhysicsMovement({ speed: 5.0 }, cannonBody, playerMesh);
     * movement.applyMovementWithMeshRotation(inputVector, cameraYaw, isSprinting);
     * 
     * // Use mesh's own rotation (like tank controls)
     * movement.applyMovementWithMeshRotation(inputVector, undefined, isSprinting, 0.15, true);
     * 
     * // World-space movement (no rotation)
     * movement.applyMovementWithMeshRotation(inputVector, undefined, isSprinting);
     * ```
     */
    applyMovementWithMeshRotation(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw?: number,
        isSprinting: boolean = false,
        rotationSmooth: number = 0.15,
        useRotation: boolean = false,
        mesh?: Object3D
    ): PhysicsVelocityData {
        // Use provided mesh or stored mesh
        const targetMesh = mesh ?? this.mesh;
        if (!targetMesh) {
            throw new Error('PhysicsMovement: mesh is required. Set via constructor, setMesh(), or pass as parameter.');
        }

        let velocityData: PhysicsVelocityData;

        // Determine which rotation mode to use
        if (cameraYaw !== undefined) {
            // External camera rotation provided - use it
            velocityData = this.applyMovementWithRotation(inputVector, cameraYaw, isSprinting);
        } else if (useRotation) {
            // Use mesh's own rotation (like applyMovementLerp)
            // For physics, don't multiply by deltaTime - physics engine handles time-stepping
            const result = this.calculateMovementFromVector(inputVector, isSprinting, 1);
            const rotatedVelocity = this.applyRotationToVelocity(targetMesh, result.velocity);

            // Apply to physics body
            this.body!.velocity.x = rotatedVelocity.x;
            this.body!.velocity.z = rotatedVelocity.z;

            velocityData = {
                x: rotatedVelocity.x,
                z: rotatedVelocity.z,
                velocity2D: result.velocity,
                direction: result.direction,
                speed: result.speed,
                isMoving: result.velocity.length() > 0.001,
            };
        } else {
            // No rotation - world-space movement
            // For physics, don't multiply by deltaTime - physics engine handles time-stepping
            const result = this.calculateMovementFromVector(inputVector, isSprinting, 1);
            this.body!.velocity.x = result.velocity.x;
            this.body!.velocity.z = result.velocity.y; // Map y to z

            velocityData = {
                x: result.velocity.x,
                z: result.velocity.y,
                velocity2D: result.velocity,
                direction: result.direction,
                speed: result.speed,
                isMoving: result.velocity.length() > 0.001,
            };
        }

        // Rotate mesh to face movement direction (only when moving and using external camera)
        if (velocityData.isMoving && cameraYaw !== undefined) {
            const targetAngle = Math.atan2(velocityData.x, velocityData.z);
            this.smoothRotateTowards(targetMesh, targetAngle, rotationSmooth);
        }

        return velocityData;
    }

    /**
     * Jump using the stored physics body (convenience method)
     * 
     * Requires body to be set via constructor or setBody().
     * For direct body parameter, use jumpWithPower(body, power) instead.
     * 
     * @param jumpPower - Optional jump power (uses config.jumpForce if not provided)
     * @returns True if jump was executed
     * @throws Error if body is not set
     */
    jump(jumpPower?: number): boolean {
        if (!this.body) {
            throw new Error('PhysicsMovement: No body set. Use setBody() or pass body in constructor.');
        }
        return this.jumpWithPower(this.body, jumpPower);
    }

    /**
     * Apply velocity to Cannon.js body using velocity.set()
     * 
     * Calculates and applies velocity to the physics body.
     * Automatically preserves Y velocity (gravity).
     * Uses body.velocity.set(x, y, z) method.
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param target - Cannon.js Body
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @returns Physics velocity data
     */
    applyVelocity(
        target: Body,
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false
    ): PhysicsVelocityData {
        // For physics, don't multiply by deltaTime - physics engine handles time-stepping
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            1
        );

        const physicsData: PhysicsVelocityData = {
            x: result.velocity.x,
            z: result.velocity.y, // Map Y to Z for 3D
            velocity2D: result.velocity,
            direction: result.direction,
            speed: result.speed,
            isMoving: result.isMoving,
        };

        // Apply velocity using .set() method (preserve Y for gravity)
        if (physicsData.isMoving) {
            const currentY = target.velocity.y;

            if (target.velocity.set) {
                // Use .set() if available: body.velocity.set(x, y, z)
                target.velocity.set(physicsData.x, currentY, physicsData.z);
            } else {
                // Fallback to direct assignment
                target.velocity.x = physicsData.x;
                target.velocity.z = physicsData.z;
            }
        }

        return physicsData;
    }

    /**
     * Get physics velocity data without applying (for manual control)
     * 
     * Returns velocity in 3D format (x, z) ready to apply to physics engine.
     * Use this if you want to manually apply or modify the velocity.
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @returns Physics velocity data
     */
    getPhysicsVelocity(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false
    ): PhysicsVelocityData {
        // For physics, don't multiply by deltaTime - physics engine handles time-stepping
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            1
        );

        return {
            x: result.velocity.x,
            z: result.velocity.y, // Map Y to Z for 3D
            velocity2D: result.velocity,
            direction: result.direction,
            speed: result.speed,
            isMoving: result.isMoving,
        };
    }

    /**
     * Apply force to Cannon.js body
     * 
     * Calculates and applies force to the physics body.
     * Use for more realistic physics-based movement.
     * 
     * @param target - Cannon.js Body with applyForce method
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param isSprinting - Whether sprint is active
     * @param forceMagnitude - Force magnitude (default: uses speed from config)
     * @param useLocalForce - Use applyLocalForce instead of applyForce
     * @returns Physics velocity data (with force values)
     */
    applyForce(
        target: Body,
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        forceMagnitude?: number,
        useLocalForce: boolean = false
    ): PhysicsVelocityData {
        // For forces, we don't use deltaTime
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            1
        );

        const magnitude = forceMagnitude ?? result.speed;

        const physicsData: PhysicsVelocityData = {
            x: result.direction.x * magnitude,
            z: result.direction.y * magnitude,
            velocity2D: result.direction.clone().multiplyScalar(magnitude),
            direction: result.direction,
            speed: magnitude,
            isMoving: result.isMoving,
        };

        // Apply force to body
        if (physicsData.isMoving) {
            const force = new Vec3(physicsData.x, 0, physicsData.z);

            if (useLocalForce && target.applyLocalForce) {
                // Apply local force (relative to body orientation)
                target.applyLocalForce(force);
            } else if (target.applyForce) {
                // Apply world force
                target.applyForce(force);
            }
        }

        return physicsData;
    }

    /**
     * Get physics velocity with manual input values (without applying)
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param forwardInput - Forward/backward input (-1 to 1)
     * @param strafeInput - Left/right input (-1 to 1)
     * @param isSprinting - Whether sprint is active
     * @returns Physics velocity data
     */
    getPhysicsVelocityRaw(
        forwardInput: number,
        strafeInput: number,
        isSprinting: boolean = false
    ): PhysicsVelocityData {
        // For physics, don't multiply by deltaTime - physics engine handles time-stepping
        const result = this.calculateMovement(
            forwardInput,
            strafeInput,
            isSprinting,
            1
        );

        return {
            x: result.velocity.x,
            z: result.velocity.y,
            velocity2D: result.velocity,
            direction: result.direction,
            speed: result.speed,
            isMoving: result.isMoving,
        };
    }

    /**
     * Jump with a specific power/force
     * Height is determined by the power applied.
     * 
     * @param target - Cannon.js Body
     * @param jumpPower - Jump power/velocity (default: uses config.jumpForce)
     * @returns True if jump was executed
     */
    jumpWithPower(
        target: Body,
        jumpPower?: number
    ): boolean {
        const now = performance.now() / 1000; // Convert to seconds

        // Check jump cooldown
        if (now - this.lastJumpTime < this.jumpCooldown) {
            return false;
        }

        // Apply jump velocity
        const jumpForce = jumpPower ?? this.config.jumpForce;
        console.log('jumpWithPower', jumpForce);
        if (target.velocity.set) {
            target.velocity.set(
                target.velocity.x,
                jumpForce,
                target.velocity.z
            );
        } else {
            target.velocity.y = jumpForce;
        }

        this.lastJumpTime = now;
        return true;
    }

    /**
     * Jump to a specific height
     * Calculates required velocity to reach the target height.
     * Uses physics formula: v = sqrt(2 * g * h)
     * 
     * Note: Assumes physics engine gravity is being applied to the body.
     * 
     * @param target - Cannon.js Body
     * @param targetHeight - Desired jump height in units
     * @param gravity - Gravity value (default: uses config.gravity, should match physics world gravity)
     * @returns True if jump was executed
     */
    jumpToHeight(
        target: Body,
        targetHeight: number,
        gravity?: number
    ): boolean {
        const now = performance.now() / 1000; // Convert to seconds

        // Check jump cooldown
        if (now - this.lastJumpTime < this.jumpCooldown) {
            return false;
        }

        // Calculate required initial velocity to reach target height
        // Using: v = sqrt(2 * |gravity| * height)
        const g = Math.abs(gravity ?? this.config.gravity);
        const requiredVelocity = Math.sqrt(2 * g * targetHeight);

        // Apply jump velocity
        if (target.velocity.set) {
            target.velocity.set(
                target.velocity.x,
                requiredVelocity,
                target.velocity.z
            );
        } else {
            target.velocity.y = requiredVelocity;
        }

        this.lastJumpTime = now;
        return true;
    }

    /**
     * Get camera-relative physics velocity
     * 
     * Calculates velocity and rotates it based on camera yaw for camera-relative movement.
     * Perfect for third-person games where movement is relative to camera direction.
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. Velocity values are in units/second.
     * 
     * @param inputVector - Input vector { x: strafe, y: forward }
     * @param cameraYaw - Camera yaw angle in radians
     * @param isSprinting - Whether sprint is active
     * @returns Physics velocity data with camera rotation applied
     * 
     * @example
     * ```typescript
     * const cameraYaw = Math.atan2(cameraDirection.x, cameraDirection.z);
     * const velocityData = movement.getPhysicsVelocityWithRotation(inputVector, cameraYaw, isSprinting);
     * body.velocity.x = velocityData.x;
     * body.velocity.z = velocityData.z;
     * ```
     */
    getPhysicsVelocityWithRotation(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw: number,
        isSprinting: boolean = false
    ): PhysicsVelocityData {
        // For physics, don't multiply by deltaTime - physics engine handles time-stepping
        const result = this.calculateMovementFromVector(
            inputVector,
            isSprinting,
            1
        );

        // Apply camera rotation to velocity
        const rotated = this.applyExternalRotationToVelocity(result.velocity, cameraYaw);

        return {
            x: rotated.x,
            z: rotated.z,
            velocity2D: result.velocity,
            direction: result.direction,
            speed: result.speed,
            isMoving: result.isMoving,
        };
    }

    /**
     * Check if physics body can jump
     * 
     * Only checks jump cooldown.
     * Grounded state should be checked by collision detection module.
     * 
     * @returns True if jump cooldown has passed
     */
    canJump(): boolean {
        const now = performance.now() / 1000;
        return now - this.lastJumpTime >= this.jumpCooldown;
    }

    /**
     * Apply jump impulse (alternative to velocity-based jump)
     * 
     * Uses impulse instead of directly setting velocity.
     * More realistic but requires mass consideration.
     * Ground detection should be handled by collision detection module.
     * 
     * @param target - Cannon.js Body with applyImpulse method
     * @param customJumpForce - Optional custom jump impulse
     * @returns True if jump was executed
     */
    jumpImpulse(
        target: Body & { applyImpulse?(impulse: { x: number; y: number; z: number }): void },
        customJumpForce?: number
    ): boolean {
        const now = performance.now() / 1000;

        // Check jump cooldown
        if (now - this.lastJumpTime < this.jumpCooldown) {
            return false;
        }

        // Apply impulse
        if (target.applyImpulse) {
            const jumpForce = customJumpForce ?? this.config.jumpForce;
            target.applyImpulse({ x: 0, y: jumpForce, z: 0 });
            this.lastJumpTime = now;
            return true;
        }

        return false;
    }

    /**
     * Set jump cooldown time
     */
    setJumpCooldown(cooldown: number): void {
        this.jumpCooldown = Math.max(0, cooldown);
    }

    /**
     * Get jump cooldown time
     */
    getJumpCooldown(): number {
        return this.jumpCooldown;
    }

}
